import { ReactElement, useEffect, useState, useCallback } from "react";
import { useRouter } from "next/router";
import { getClientCookie } from "@/utils/cookies/client";
import styles from "./CookiePolicy.module.css";

export const CookiePolicy = ({
    autoDismiss,
    cookiePolicyText,
}: {
    autoDismiss: boolean;
    cookiePolicyText: string;
}): ReactElement | null => {
    const router = useRouter();
    const [isOpen, setIsOpen] = useState(false);

    const isBingTraffic = useCallback((): boolean => {
        const { network } = router.query;
        
        // Check query string for network=bing
        if (network === "bing") {
            return true;
        }

        // Check session cookie for network:bing
        try {
            const sessionCookie = getClientCookie("SESSION_COOKIE_NAME");
            if (sessionCookie !== "{}") {
                const sessionData = JSON.parse(sessionCookie);
                if (sessionData.network === "bing") {
                    return true;
                }
            }
        } catch (error) {
            console.error("Error parsing session cookie:", error);
        }

        return false;
    }, [router.query]);

    useEffect(() => {
        // Don't show cookie banner for Bing traffic or if the user has already closed the banner
        const isComingFromBing = isBingTraffic();
        if (isComingFromBing || localStorage?.getItem("cookie-banner-is-closed")) {
            setIsOpen(false);
            return;
        } else {
            setIsOpen(true);
        }
    }, [isBingTraffic]);

    useEffect(() => {
        if (!autoDismiss) return;

        const onRouteChange = () => close();
        window.addEventListener("beforeunload", onRouteChange);
        router.events.on("routeChangeComplete", onRouteChange);
        
        return () => {
            window.removeEventListener("beforeunload", onRouteChange);
            router.events.off("routeChangeComplete", onRouteChange);
        };
    }, [router, autoDismiss]);

    const close = () => {
        localStorage.setItem("cookie-banner-is-closed", "closed");
        setIsOpen(false);
    };

    return isOpen ? (
        <div className={styles.cookie}>
            <div className={styles.content}>
                <div
                    dangerouslySetInnerHTML={{
                        __html: cookiePolicyText,
                    }}
                />
                <button onClick={close}>OK</button>
            </div>
        </div>
    ) : null;
};
